import {
	Component,
	input,
	linkedSignal,
	resource,
	signal,
	computed,
	effect,
	viewChild,
} from '@angular/core'
import {
	BoInfoSymbol,
	ReportClass,
	ReportView,
	ReportViewFilter,
	ReportViewAggregation,
	ReportViewFilterOperator,
	ReportViewAggregationOperator,
	ReportViewAggregationOperators,
	ReportViewFilterOperators,
	ReportViewSortDirection,
	ReportResult,
	ReportInfo,
	capitalizeFirstLetter,
} from '@lg/shared'
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop'
import { ReportExportComponent } from './report-export/report-export.component.js'
import type { ReportExportData } from './report-export/report-export.component.js'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { ReactiveFormsModule } from '@angular/forms'
import { MaterialModules } from '../../material.modules.js'
import { inject } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { PageEvent } from '@angular/material/paginator'
import { ChevronComponent } from '../chevron/chevron.component.js'
import { ChevronStepComponent } from '../chevron/chevron-step.component.js'
import { MatMenu, MatMenuTrigger } from '@angular/material/menu'
import {
	ArcElement,
	Chart,
	ChartConfiguration,
	ChartData,
	Legend,
	PieController,
	Title,
} from 'chart.js'
import { BaseChartDirective } from 'ng2-charts'
import DataLabelsPlugin from 'chartjs-plugin-datalabels'

const ViewTypeOptions = {
	drillDownChart: 'Drill down chart',
	aggregated: 'Aggregated list',
	fullData: 'Full data',
} as const

@Component({
	selector: 'app-report',
	templateUrl: './report.component.html',
	styleUrls: ['./report.component.scss'],
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MaterialModules,
		ChevronComponent,
		ChevronStepComponent,
		BaseChartDirective,
	],
})
export class ReportComponent {
	private dialog = inject(MatDialog)
	cellContextMenuTrigger = viewChild<MatMenuTrigger>('cellContextMenuTrigger')
	reportInput = input.required<ReportClass>({ alias: 'report' })
	viewInput = input.required<string>({ alias: 'view' })
	viewTypeInput = input<keyof typeof ViewTypeOptions>('drillDownChart', {
		alias: 'viewType',
	})
	showOptions = input(true)
	optionsInput = input<ReportView>(
		{
			filters: [],
			sorts: [],
			aggregations: [],
			groupBy: [],
		},
		{ alias: 'options' },
	)
	allowExpandingRowsInput = input(true)

	report = linkedSignal(() => this.reportInput())
	staticViews = computed(
		() => (this.report()[BoInfoSymbol] as ReportInfo).staticViews,
	)
	view = linkedSignal(() =>
		this.staticViews().find((v) => v.name === this.viewInput()),
	)
	viewDefaultGroupBy = computed(() => this.view()?.groupBy ?? [])
	options = linkedSignal(() => this.optionsInput())
	disabledGroupBys = linkedSignal<Set<string>>(() => new Set(), {
		equal: (a, b) => false,
	})
	availableGroupBy = linkedSignal(() => {
		const mergedGroupBy = [
			...this.viewDefaultGroupBy(),
			...this.options().groupBy,
		]
		return Array.from(new Set(mergedGroupBy))
	})
	effectiveGroupBy = computed(() =>
		this.availableGroupBy().filter((g) => !this.disabledGroupBys().has(g)),
	)
	viewTypeOptions = Object.entries(ViewTypeOptions)
	viewType = linkedSignal(this.viewTypeInput)
	allowExpandingRows = computed(
		() => this.allowExpandingRowsInput() && this.viewType() !== 'fullData',
	)
	drilldownFilters = signal<DrilldownFilter[]>([], { equal: (a, b) => false })
	results = resource({
		params: () => {
			return {
				report: this.report(),
				view: this.view(),
				options: this.options(),
				viewType: this.viewType(),
				pageSize: this.paginationPageSize(),
				pageIndex: this.paginationPageIndex(),
				drilldownFilter: this.drilldownFilters(),
				effectiveGroupBy: this.effectiveGroupBy(),
			}
		},
		loader: ({ params }) => {
			const methodName = `${params.view.name}${params.viewType === 'fullData' ? '_fullData' : ''}`
			const pagedOptions = {
				...params.options,
				filters: params.options.filters.slice(),
				groupBy: params.effectiveGroupBy,
				pageSize: params.pageSize,
				pageIndex: params.pageIndex,
			}
			if (params.viewType === 'drillDownChart') {
				for (const filter of params.drilldownFilter) {
					if (filter.isActive) {
						pagedOptions.filters.push({
							property: filter.property,
							operator: 'eq',
							values: filter.values,
						})
					}
				}
			}
			return params.report[methodName](pagedOptions) as Promise<ReportResult>
		},
	})
	columns = computed(() => {
		const value = this.results.value()
		if (!value) return []
		return value.columns
	})

	rows = computed(() => {
		const value = this.results.value()
		if (!value) return []
		return value.list
	})

	rowDetailOptions = linkedSignal<WeakMap<object, ReportView>>(
		() => {
			// map of row object to its detail options
			this.rows() // reset on each reload of the results
			return new WeakMap()
		},
		{ equal: (a, b) => false },
	)
	expandedRows = linkedSignal<Set<object>>(
		() => {
			this.rows()
			return new Set()
		},
		{ equal: (a, b) => false },
	)
	paginationLength = computed(() => {
		const value = this.results.value()
		if (!value) return 0
		return value.length
	})
	paginationPageSizeOptions = [10, 20, 50, 100]
	paginationPageSize = signal(this.paginationPageSizeOptions[0])
	paginationPageIndex = signal(0)

	displayedColumns = computed(() => {
		return this.columns()
	})
	displayedColumnProperties = computed(() => {
		return [
			...(this.allowExpandingRows() ? ['$expand'] : []),
			...this.displayedColumns().map((c) => c.property),
		]
	})

	availableColumns = computed(() => {
		return this.report().baseEntity[BoInfoSymbol].allPropertyNames
	})

	// Computed signals for available columns (excluding already used ones)
	availableForFilters = computed(() => {
		const used = new Set(this.options().filters.map((f) => f.property))
		return this.availableColumns().filter((col) => !used.has(col))
	})

	availableForGroupBy = computed(() => {
		const used = new Set(this.options().groupBy)
		return this.availableColumns().filter((col) => !used.has(col))
	})

	availableForAggregations = computed(() => {
		const used = new Set(this.options().aggregations.map((a) => a.property))
		return this.availableColumns().filter((col) => !used.has(col))
	})

	// Filter operators
	filterOperators: readonly ReportViewFilterOperator[] =
		ReportViewFilterOperators

	// Aggregation operators
	aggregationOperators: readonly ReportViewAggregationOperator[] =
		ReportViewAggregationOperators

	// Current sort (first in sorts array) for display purposes
	currentSort = computed(() => {
		const sorts = this.options().sorts
		return sorts.length > 0 ? sorts[0] : null
	})

	drilldownChartConfigurations = computed(() => {
		const aggregations = [
			...(this.view()?.aggregations ?? []),
			...(this.options().aggregations ?? []),
		]
		const groupBy = this.effectiveGroupBy()
		const rows = this.rows()
		return aggregations.map((agg) => {
			const labels = rows.map((row) =>
				groupBy.map((groupBy) => `${groupBy}: ${row[groupBy]}`).join(', '),
			)
			return {
				type: 'pie',
				data: {
					labels,
					datasets: [
						{
							data: this.rows().map(
								(r) => r[agg.outputName || `${agg.property}_${agg.operator}`],
							),
							backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56'],
						},
					],
				} as ChartData,
				options: {
					animation: {
						duration: 250,
					},
					responsive: true,
					plugins: {
						title: {
							display: true,
							text: `[${capitalizeFirstLetter(agg.operator)}] ${(agg.outputName || agg.property).replaceAll('$', '.')}`,
							font: { size: 20 },
						},
						legend: {
							display: true,
							position: 'right',
						},
					},
					onClick: (event, elements) => {
						console.log(event, elements)
					},
				},
			} as ChartConfiguration
		})
	})

	constructor() {
		Chart.register(PieController, ArcElement, Title, Legend, DataLabelsPlugin)
		effect(() => {
			if (
				this.results.hasValue() &&
				this.results.value()?.pageIndex !== this.paginationPageIndex()
			) {
				this.paginationPageIndex.set(this.results.value()?.pageIndex ?? 0)
			}
		})
	}

	// Get sort direction for a column (only shows for current sort)
	getSortDirection(column: string): ReportViewSortDirection | null {
		const current = this.currentSort()
		return current && current.property === column ? current.direction : null
	}

	// Handle column header click for sorting
	sortColumn(column: string, event?: MouseEvent) {
		const isShiftHeld = event?.shiftKey ?? false
		const sorts = [...this.options().sorts]
		const existingIndex = sorts.findIndex((s) => s.property === column)

		if (!isShiftHeld) {
			// Single sort mode: only one column can be sorted at a time
			if (existingIndex === 0) {
				// Column is already the first sort - toggle direction or remove
				const current = sorts[0]
				if (current.direction === 'ASC') {
					// Change to DESC
					this.options.update((opts) => ({
						...opts,
						sorts: [{ ...current, direction: 'DESC' }],
					}))
				} else {
					// Remove from sorts
					this.options.update((opts) => ({
						...opts,
						sorts: [],
					}))
				}
			} else {
				// Column is not the first sort (or not in sorts) - replace all sorts with this one
				this.options.update((opts) => ({
					...opts,
					sorts: [{ property: column, direction: 'ASC' }],
				}))
			}
		} else {
			// Multi-sort mode: keep current logic for multiple sort columns
			if (existingIndex === 0) {
				// Column is already the first sort - toggle direction or remove
				const current = sorts[0]
				if (current.direction === 'ASC') {
					// Change to DESC
					sorts[0] = { ...current, direction: 'DESC' }
				} else {
					// Remove from sorts
					sorts.shift()
				}
			} else if (existingIndex > 0) {
				// Column is in sorts but not first - move to first
				const sort = sorts.splice(existingIndex, 1)[0]
				sorts.unshift(sort)
			} else {
				// Column is not in sorts - add as first with ASC
				sorts.unshift({ property: column, direction: 'ASC' })
			}

			this.options.update((opts) => ({
				...opts,
				sorts,
			}))
		}
	}

	// Drag & drop handlers
	dropFilter(event: CdkDragDrop<any>) {
		let filters = this.options().filters.slice()
		if (event.previousContainer === event.container) {
			moveItemInArray(filters, event.previousIndex, event.currentIndex)
			this.options.update((opts) => ({
				...opts,
				filters,
			}))
		} else {
			const column = event.previousContainer.data[event.previousIndex] as string
			const isAdding = event.container.data === this.options().filters
			if (isAdding) {
				const isFilterSet = this.options().filters.some(
					(f) => f.property === column,
				)
				if (isFilterSet) return
				filters.push({
					property: column,
					operator: 'eq',
					values: [''],
				})
			} else {
				filters = filters.filter((f) => f.property !== column)
			}
			this.options.update((opts) => ({
				...opts,
				filters,
			}))
		}
	}

	dropGroupBy(event: CdkDragDrop<any>) {
		if (event.previousContainer === event.container) {
			const groupBy = [...this.options().groupBy]
			moveItemInArray(groupBy, event.previousIndex, event.currentIndex)
			this.options.update((opts) => ({
				...opts,
				groupBy,
			}))
		} else {
			const column = event.previousContainer.data[event.previousIndex] as string
			this.options.update((opts) => ({
				...opts,
				groupBy: [...opts.groupBy, column],
			}))
		}
	}

	dropAggregation(event: CdkDragDrop<any>) {
		if (event.previousContainer === event.container) {
			const aggregations = [...this.options().aggregations]
			moveItemInArray(aggregations, event.previousIndex, event.currentIndex)
			this.options.update((opts) => ({
				...opts,
				aggregations,
			}))
		} else {
			const column = event.previousContainer.data[event.previousIndex] as string
			const newAggregation: ReportViewAggregation = {
				property: column,
				operator: 'sum',
				outputName: `${column}_sum`,
			}
			this.options.update((opts) => ({
				...opts,
				aggregations: [...opts.aggregations, newAggregation],
			}))
		}
	}

	getRowDetailOptions(row: object): ReportView {
		const rowDetailOptions = this.rowDetailOptions()
		if (rowDetailOptions.has(row)) {
			return rowDetailOptions.get(row)!
		}

		const options = this.options()
		// const mergedOptions = this.report()[`${this.view()?.name}$options`](options)

		const detailOptions: ReportView = {
			...this.options(),
			filters: [...options.filters],
		}
		for (const groupBy of this.effectiveGroupBy()) {
			detailOptions.filters.push({
				property: groupBy,
				operator: 'eq',
				values: [row[groupBy]],
			})
		}

		rowDetailOptions.set(row, detailOptions)
		return detailOptions
	}

	toggleExpandRow(row: object) {
		this.expandedRows.update((expandedRows) => {
			if (expandedRows.has(row)) {
				expandedRows.delete(row)
			} else {
				expandedRows.add(row)
			}
			return expandedRows
		})
	}

	// Helper method for parsing array values
	parseArrayValue(value: string): string[] {
		return value
			.split(',')
			.map((v) => v.trim())
			.filter((v) => v.length > 0)
	}

	// Helper method for formatting array values for display
	formatArrayValue(value: any): string {
		if (Array.isArray(value)) {
			return value.join(', ')
		}
		return String(value || '')
	}

	// Remove handlers
	removeFilter(index: number) {
		this.options.update((opts) => ({
			...opts,
			filters: opts.filters.filter((_, i) => i !== index),
		}))
	}

	removeGroupBy(index: number) {
		this.options.update((opts) => ({
			...opts,
			groupBy: opts.groupBy.filter((_, i) => i !== index),
		}))
	}

	removeAggregation(index: number) {
		this.options.update((opts) => ({
			...opts,
			aggregations: opts.aggregations.filter((_, i) => i !== index),
		}))
	}

	// Update handlers
	updateFilterOperator(index: number, operator: ReportViewFilterOperator) {
		this.options.update((opts) => ({
			...opts,
			filters: opts.filters.map((f, i) =>
				i === index
					? { ...f, operator, value: this.getDefaultFilterValue(operator) }
					: f,
			),
		}))
	}

	updateFilterValue(filterIndex: number, valueIndex: number, value: any) {
		this.options.update((opts) => ({
			...opts,
			filters: opts.filters.map((f, i) =>
				i === filterIndex
					? {
							...f,
							values: f.values.map((v, j) => (j === valueIndex ? value : v)),
						}
					: f,
			),
		}))
	}

	updateAggregationOperator(
		index: number,
		operator: ReportViewAggregationOperator,
	) {
		this.options.update((opts) => ({
			...opts,
			aggregations: opts.aggregations.map((a, i) =>
				i === index ? { ...a, operator } : a,
			),
		}))
	}

	updateAggregationOutputName(index: number, outputName: string) {
		this.options.update((opts) => ({
			...opts,
			aggregations: opts.aggregations.map((a, i) =>
				i === index ? { ...a, outputName } : a,
			),
		}))
	}

	toggleDisabledGroupBy(groupBy: string) {
		this.disabledGroupBys.update((disabledGroupBys) => {
			if (disabledGroupBys.has(groupBy)) {
				disabledGroupBys.delete(groupBy)
			} else {
				disabledGroupBys.add(groupBy)
			}
			return disabledGroupBys
		})
	}

	reorderAvailableGroupBy(event: CdkDragDrop<string[]>) {
		const availableGroupBy = this.availableGroupBy().slice()
		moveItemInArray(availableGroupBy, event.previousIndex, event.currentIndex)
		this.availableGroupBy.set(availableGroupBy)
	}

	private getDefaultFilterValue(operator: ReportViewFilterOperator): any {
		if (operator === 'is null' || operator === 'is not null') {
			return [null]
		}
		if (operator === 'in' || operator === 'not in') {
			return []
		}
		if (operator === 'between' || operator === 'not between') {
			return []
		}
		return ['']
	}

	onPage(event: PageEvent) {
		this.paginationPageSize.set(event.pageSize)
		this.paginationPageIndex.set(event.pageIndex)
	}

	openExportDialog() {
		const reportName = this.view()?.description
		this.dialog.open(ReportExportComponent, {
			data: {
				reportName,
			},
		})
	}

	addDrilldownFilter(
		property: string,
		value: any,
		isAppendValue: boolean,
		isExcludeValue: boolean,
	) {
		const filters = this.drilldownFilters()

		const filter = filters.find((f) => f.property === property)
		if (filter) {
			let valArray = isExcludeValue ? filter.excludeValues : filter.values
			if (isAppendValue) {
				if (valArray.includes(value)) return
				valArray.push(value)
			} else {
				valArray = [value]
			}
			if (isExcludeValue) {
				filter.excludeValues = valArray
			} else {
				filter.values = valArray
			}
		} else {
			filters.push({
				property,
				values: isExcludeValue ? [] : [value],
				excludeValues: isExcludeValue ? [value] : [],
				isActive: true,
			})
		}

		this.drilldownFilters.update((x) => x)
	}

	removeDrilldownFilter(index: number) {
		this.drilldownFilters.update((drilldownFilter) =>
			drilldownFilter.filter((_, i) => i !== index),
		)
	}

	removeDrilldownFiltersAfter(index: number) {
		this.drilldownFilters.update((drilldownFilter) =>
			drilldownFilter.slice(0, index + 1),
		)
	}

	toggleDrilldownFilterActive(index: number) {
		this.drilldownFilters.update((drilldownFilter) =>
			drilldownFilter.map((f, i) =>
				i === index ? { ...f, isActive: !f.isActive } : f,
			),
		)
	}

	getDrilldownFilterDisplayString(filter: DrilldownFilter): string {
		return [
			...filter.values,
			...filter.excludeValues.map((v) => `NOT(${v})`),
		].join(', ')
	}

	onCellContextMenu(
		event: MouseEvent,
		column: ReportResult['columns'][number],
		row: any,
	) {
		if (this.viewType() !== 'drillDownChart') return

		event.preventDefault()
		const trigger = this.cellContextMenuTrigger()
		trigger.menuData = {
			column,
			row,
		}
		trigger.openMenu()
	}
}

interface DrilldownFilter {
	property: string
	values: any[]
	excludeValues: any[]
	isActive: boolean
}
